import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ExternalLink, CheckCircle2, AlertTriangle, ChevronRight, Info } from "lucide-react";
import { api, formatPrice, BADGES, usePageMeta } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { CookieBanner } from "@/components/CookieBanner";
import { Reveal } from "@/components/Reveal";

export default function ProductPage() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  usePageMeta(
    product ? `${product.name} · solsdiarias360` : "Producto · solsdiarias360",
    product?.solution
  );

  useEffect(() => {
    setProduct(null);
    setError(false);
    setActiveImg(0);
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setError(true));
  }, [id]);

  const track = () => api.post(`/products/${id}/click`).catch(() => {});
  const badge = product?.badge ? BADGES[product.badge] : null;
  const photos = product ? [product.image, ...(product.images || [])].filter(Boolean) : [];

  return (
    <div className="App">
      <Header />
      <main className="bg-white min-h-screen">
        {error && (
          <div className="max-w-3xl mx-auto px-4 py-24 text-center">
            <h1 className="font-display text-2xl font-bold text-slate-900">Producto no encontrado</h1>
            <Link to="/" className="text-amber-600 font-semibold text-sm mt-4 inline-block">Volver al inicio</Link>
          </div>
        )}

        {product && (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <nav className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap" data-testid="product-breadcrumb">
              <Link to="/" className="hover:text-amber-600">Inicio</Link>
              <ChevronRight size={12} />
              <Link to={`/categoria/${product.category}`} className="hover:text-amber-600 capitalize">
                {product.category.replace(/-/g, " ")}
              </Link>
              <ChevronRight size={12} />
              <span className="text-slate-800 font-medium line-clamp-1">{product.name}</span>
            </nav>

            <div className="grid md:grid-cols-2 gap-10 mt-8">
              <div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6 }}
                  className="relative bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden"
                >
                  <img src={photos[activeImg] || product.image} alt={product.name} className="w-full h-full max-h-[480px] object-cover" data-testid="product-main-image" />
                  <div className="absolute top-3 left-3 flex flex-col gap-1 items-start">
                    {badge && <span className={`text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md border ${badge.cls}`}>{badge.label}</span>}
                    {product.provisional && (
                      <span className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-md bg-slate-900/85 text-white">Muestra provisional</span>
                    )}
                  </div>
                </motion.div>
                {photos.length > 1 && (
                  <div className="flex gap-2 mt-3 flex-wrap" data-testid="product-gallery-thumbnails">
                    {photos.map((src, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setActiveImg(i)}
                        data-testid={`product-thumbnail-${i}`}
                        aria-label={`Ver foto ${i + 1}`}
                        className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors ${i === activeImg ? "border-amber-500" : "border-slate-200 hover:border-slate-400"}`}
                      >
                        <img src={src} alt={`${product.name} · foto ${i + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
                <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight leading-tight" data-testid="product-title">
                  {product.name}
                </h1>

                {product.provisional && (
                  <p className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="provisional-notice">
                    <Info size={14} className="shrink-0 mt-0.5" />
                    Ficha de ejemplo del catálogo provisional. La imagen, el precio y el enlace se
                    sustituirán por los datos verificados del producto real.
                  </p>
                )}

                {product.price != null && (
                  <p className="font-display text-3xl font-bold text-slate-900 mt-5" data-testid="product-price">
                    {formatPrice(product.price)}
                  </p>
                )}

                <div className="mt-6 space-y-3">
                  {product.problem && (
                    <div className="bg-slate-50 border-l-4 border-amber-500 rounded-r-xl p-4">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1.5">
                        <AlertTriangle size={12} className="text-amber-500" /> El problema
                      </p>
                      <p className="text-sm text-slate-700 leading-relaxed">{product.problem}</p>
                    </div>
                  )}
                  {product.solution && (
                    <div className="bg-slate-50 border-l-4 border-emerald-500 rounded-r-xl p-4">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1 flex items-center gap-1.5">
                        <CheckCircle2 size={12} className="text-emerald-500" /> La solución
                      </p>
                      <p className="text-sm text-slate-700 leading-relaxed">{product.solution}</p>
                    </div>
                  )}
                </div>

                {product.description && (
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed mt-6">{product.description}</p>
                )}

                {product.amazon_url ? (
                  <a
                    href={product.amazon_url}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    onClick={track}
                    data-testid="buy-amazon-button"
                    className="mt-8 w-full sm:w-auto bg-amber-500 hover:bg-amber-400 active:scale-[0.98] text-slate-950 font-bold px-8 py-4 rounded-xl inline-flex items-center justify-center gap-2 transition-[background-color,transform] shadow-lg shadow-amber-500/20"
                  >
                    Comprar <ExternalLink size={17} strokeWidth={2.5} />
                  </a>
                ) : (
                  <span className="mt-8 w-full sm:w-auto bg-slate-100 text-slate-400 font-semibold px-8 py-4 rounded-xl inline-flex items-center gap-2 cursor-not-allowed">
                    <Info size={16} /> Enlace disponible próximamente
                  </span>
                )}
                <p className="text-[11px] text-slate-500 mt-3 leading-relaxed max-w-md" data-testid="product-affiliate-note">
                  El precio y la disponibilidad definitivos son los que muestre Amazon.es en el
                  momento de la compra.
                </p>
              </motion.div>
            </div>

            {product.related?.length > 0 && (
              <section className="mt-20" data-testid="related-products">
                <Reveal>
                  <h2 className="font-display text-2xl font-bold text-slate-900 tracking-tight">También te puede interesar</h2>
                </Reveal>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
                  {product.related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
                </div>
              </section>
            )}
          </div>
        )}
      </main>
      <Footer />
      <CookieBanner />
    </div>
  );
}
